import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { Notifier } from '../../../../../../system/services/notificationManager';
import { useAuthorizer } from '../../../../../../auth/AuthorizationContext';
import {
  Maybe,
  useClearThermostatUpdateStatusMutation,
} from '../../../../../../../types/generated-types';

interface UpdatePendingProps {
  deviceId?: string | Maybe<string> | undefined | null;
  status: 'submitted' | 'awaiting';
  padding?: number;
  getClearContext?: () => number;
  onCleared?: (deviceId: string, clearContext?: number) => void;
}

export function UpdatePending({
  deviceId,
  status = 'submitted',
  padding = 2,
  getClearContext,
  onCleared,
}: UpdatePendingProps) {
  const { can } = useAuthorizer();
  const [isClearing, setIsClearing] = React.useState(false);
  const [clearThermostatUpdateStatus] = useClearThermostatUpdateStatusMutation({
    refetchQueries: ['ThermostatDetail'],
  });

  const handleClearThermostatUpdateStatusHandler = async () => {
    if (isClearing || !can('be', 'super')) return;
    setIsClearing(true);
    try {
      if (deviceId) {
        const clearContext = getClearContext?.();
        const result = await clearThermostatUpdateStatus({
          variables: { deviceId: deviceId || '' },
        });
        const clearedThermostat = result?.data?.clearThermostatUpdateStatus;
        if (
          clearedThermostat?.hasPendingUpdates === false &&
          clearedThermostat.deviceId === deviceId
        ) {
          onCleared?.(deviceId, clearContext);
        }
        Notifier.success('Update status cleared successfully');
      } else {
        Notifier.error('No device id available.');
      }
    } catch (error) {
      Notifier.error('Failed to clear update status');
      console.error('Error clearing update status:', error);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Box
      alignItems={'center'}
      justifyContent={'center'}
      sx={{
        padding: `${padding}px 10px`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CircularProgress color={status === 'submitted' ? 'info' : 'success'} />
      {deviceId && can('be', 'super') ? (
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearThermostatUpdateStatusHandler}
          disabled={isClearing}
          aria-busy={isClearing}
          sx={{ my: 1 }}
        >
          Clear update status
        </Button>
      ) : null}
      <div>
        {status === 'submitted'
          ? 'Sending request to device ...'
          : 'Awaiting configuration change confirmation'}
      </div>
    </Box>
  );
}
